/**
 * Serviço para Cálculo de Evapotranspiração de Referência (ET0)
 * 
 * Métodos implementados:
 * 1. Penman-Monteith FAO-56 (padrão internacional - precisa de todos os dados)
 * 2. Hargreaves-Samani (alternativo - apenas temperatura min/max)
 * 
 * Referências:
 * - FAO Irrigation and Drainage Paper 56 (Allen et al., 1998)
 * - Hargreaves & Samani (1985)
 */

export class ET0Calculator {
  
  /**
   * Método 1: PENMAN-MONTEITH FAO-56 (Padrão Ouro)
   * Requer: Temperatura, umidade, vento, radiação solar
   * 
   * @param {number} tempMax - Temperatura máxima (°C)
   * @param {number} tempMin - Temperatura mínima (°C)
   * @param {number} umidade - Umidade relativa média (%)
   * @param {number} vento2m - Velocidade do vento a 2m (m/s)
   * @param {number} radiacaoSolar - Radiação solar (MJ/m²/dia)
   * @param {number} latitude - Latitude do local (graus decimais)
   * @param {number} altitude - Altitude (metros)
   * @param {Date} data - Data para cálculo do dia juliano
   * @returns {number} ET0 em mm/dia
   */
  static calcularPenmanMonteith(
    tempMax,
    tempMin,
    umidade,
    vento2m = 2.0,
    radiacaoSolar = null,
    latitude = -20,
    altitude = 800,
    data = new Date()
  ) {
    // Validações
    if (!this.validarTemperatura(tempMax) || !this.validarTemperatura(tempMin)) {
      console.warn('⚠️ Temperaturas inválidas para Penman-Monteith');
      return null;
    }
    
    if (tempMax < tempMin) {
      console.warn('⚠️ Temp. máxima menor que mínima');
      return null;
    }
    
    // Temperatura média
    const tempMedia = (tempMax + tempMin) / 2;
    
    // Constantes
    const gamma = this.calcularConstantePsicrometrica(altitude);
    const delta = this.calcularInclinacaoCurvaPressaoVapor(tempMedia);
    
    // Pressão de vapor de saturação
    const es_max = 0.6108 * Math.exp((17.27 * tempMax) / (tempMax + 237.3));
    const es_min = 0.6108 * Math.exp((17.27 * tempMin) / (tempMin + 237.3));
    const es = (es_max + es_min) / 2;
    
    // Pressão de vapor atual
    const umidadeValida = umidade >= 0 && umidade <= 100 ? umidade : 50;
    const ea = es * (umidadeValida / 100);
    
    // Déficit de pressão de vapor
    const dpv = es - ea;
    
    // Velocidade do vento (default 2 m/s se não fornecido)
    const u2 = vento2m >= 0 && vento2m <= 20 ? vento2m : 2.0;
    
    // Radiação (estimada se não fornecida)
    let Rs = radiacaoSolar;
    if (!Rs || Rs <= 0) {
      Rs = this.estimarRadiacaoSolar(tempMax, tempMin, latitude, data);
    }
    
    // Radiação extraterrestre (Ra)
    const Ra = this.calcularRadiacaoExtraterrestre(latitude, data);
    
    // Radiação de ondas curtas líquida (Rns)
    const albedo = 0.23; // Grama de referência
    const Rns = (1 - albedo) * Rs;
    
    // Radiação de ondas longas líquida (Rnl)
    const stefan_boltzmann = 4.903e-9; // MJ K⁻⁴ m⁻² dia⁻¹
    const Rnl = stefan_boltzmann * 
      (Math.pow(tempMax + 273.16, 4) + Math.pow(tempMin + 273.16, 4)) / 2 *
      (0.34 - 0.14 * Math.sqrt(ea)) *
      (1.35 * Math.min(Rs / Ra, 1.0) - 0.35);
    
    // Radiação líquida total (Rn)
    const Rn = Math.max(0, Rns - Rnl);
    
    // Fluxo de calor no solo (G) = 0 para período diário
    const G = 0;
    
    // Fórmula de Penman-Monteith (FAO-56)
    const numerador = 0.408 * delta * (Rn - G) + 
                      gamma * (900 / (tempMedia + 273)) * u2 * dpv;
    const denominador = delta + gamma * (1 + 0.34 * u2);
    
    const ET0 = numerador / denominador;
    
    // Validação do resultado
    if (ET0 < 0 || ET0 > 20) {
      console.warn(`⚠️ ET0 fora do esperado: ${ET0.toFixed(2)} mm/dia`);
      return null;
    }
    
    return parseFloat(ET0.toFixed(2));
  }
  
  /**
   * Método 2: HARGREAVES-SAMANI (Método Alternativo)
   * Requer apenas: Temperatura máxima, mínima e latitude
   * Útil quando não há dados de umidade, vento ou radiação
   * Precisão: ~80-85% comparado a Penman-Monteith
   * 
   * @param {number} tempMax - Temperatura máxima (°C)
   * @param {number} tempMin - Temperatura mínima (°C)
   * @param {number} latitude - Latitude do local (graus decimais)
   * @param {Date} data - Data para cálculo
   * @returns {number} ET0 em mm/dia
   */
  static calcularHargreavesSamani(
    tempMax,
    tempMin,
    latitude = -20,
    data = new Date()
  ) {
    // Validações
    if (!this.validarTemperatura(tempMax) || !this.validarTemperatura(tempMin)) {
      console.warn('⚠️ Temperaturas inválidas para Hargreaves-Samani');
      return null;
    }
    
    // Se max == min (dados parciais do dia), assume amplitude mínima de 4°C
    if (tempMax <= tempMin) {
      tempMax = tempMin + 4;
    }
    
    // Temperatura média
    const tempMedia = (tempMax + tempMin) / 2;
    
    // Amplitude térmica
    const amplitudeTermica = tempMax - tempMin;
    
    // Radiação extraterrestre (Ra) em mm/dia equivalente
    const Ra = this.calcularRadiacaoExtraterrestreEquivalente(latitude, data);
    
    // Coeficiente de Hargreaves (original = 0.0023)
    // Ajustado para condições tropicais brasileiras
    const coeficiente = 0.0023;
    const expoente = 0.5;
    
    // Fórmula de Hargreaves-Samani
    // ET0 = 0.0023 × Ra × (Tmean + 17.8) × √(Tmax - Tmin)
    const ET0 = coeficiente * Ra * (tempMedia + 17.8) * Math.pow(amplitudeTermica, expoente);
    
    // Validação do resultado
    if (ET0 < 0 || ET0 > 20) {
      console.warn(`⚠️ ET0 (HS) fora do esperado: ${ET0.toFixed(2)} mm/dia`);
      return null;
    }
    
    return parseFloat(ET0.toFixed(2));
  }
  
  /**
   * Método Automático: Seleciona o melhor método disponível
   * Prioridade: Penman-Monteith > Hargreaves-Samani
   * 
   * @param {Object} dados - Dados meteorológicos disponíveis
   * @returns {Object} { et0, metodo, qualidade }
   */
  static calcularET0Automatico(dados) {
    const {
      tempMax,
      tempMin,
      umidade,
      vento,
      radiacao,
      latitude = -20,
      altitude = 800,
      data = new Date()
    } = dados;
    
    // Tenta Penman-Monteith se houver dados suficientes
    if (umidade !== null && umidade !== undefined) {
      const et0PM = this.calcularPenmanMonteith(
        tempMax,
        tempMin,
        umidade,
        vento,
        radiacao,
        latitude,
        altitude,
        data
      );
      
      if (et0PM !== null) {
        return {
          et0: et0PM,
          metodo: 'Penman-Monteith',
          qualidade: radiacao && vento ? 'excelente' : 'boa'
        };
      }
    }
    
    // Fallback para Hargreaves-Samani (apenas temperatura)
    const et0HS = this.calcularHargreavesSamani(
      tempMax,
      tempMin,
      latitude,
      data
    );
    
    if (et0HS !== null) {
      return {
        et0: et0HS,
        metodo: 'Hargreaves-Samani',
        qualidade: 'aceitavel'
      };
    }
    
    // Nenhum método disponível
    return {
      et0: null,
      metodo: 'nenhum',
      qualidade: 'insuficiente',
      erro: 'Dados insuficientes para cálculo de ET0'
    };
  }
  
  // ==================== FUNÇÕES AUXILIARES ====================
  
  /**
   * Calcular constante psicrométrica (γ)
   * γ = 0.000665 × P
   * onde P é a pressão atmosférica (kPa)
   */
  static calcularConstantePsicrometrica(altitude) {
    // Pressão atmosférica em função da altitude
    const P = 101.3 * Math.pow((293 - 0.0065 * altitude) / 293, 5.26);
    return 0.000665 * P;
  }
  
  /**
   * Calcular inclinação da curva de pressão de vapor (Δ)
   */
  static calcularInclinacaoCurvaPressaoVapor(tempMedia) {
    const es = 0.6108 * Math.exp((17.27 * tempMedia) / (tempMedia + 237.3));
    return (4098 * es) / Math.pow(tempMedia + 237.3, 2);
  }
  
  /**
   * Calcular radiação extraterrestre (Ra)
   * Baseado na latitude e dia do ano
   */
  static calcularRadiacaoExtraterrestre(latitude, data) {
    const latRad = (Math.PI / 180) * latitude;
    const diaJuliano = this.calcularDiaJuliano(data);
    
    // Declinação solar (δ)
    const declinacao = 0.409 * Math.sin((2 * Math.PI / 365) * diaJuliano - 1.39);
    
    // Distância relativa Terra-Sol (dr)
    const dr = 1 + 0.033 * Math.cos((2 * Math.PI / 365) * diaJuliano);
    
    // Ângulo de pôr do sol (ωs)
    const ws = Math.acos(-Math.tan(latRad) * Math.tan(declinacao));
    
    // Radiação extraterrestre (Ra) em MJ/m²/dia
    const Gsc = 0.0820; // Constante solar (MJ/m²/min)
    const Ra = (24 * 60 / Math.PI) * Gsc * dr * 
      (ws * Math.sin(latRad) * Math.sin(declinacao) + 
       Math.cos(latRad) * Math.cos(declinacao) * Math.sin(ws));
    
    return Math.max(0, Ra);
  }
  
  /**
   * Radiação extraterrestre em equivalente de evaporação (mm/dia)
   * Para Hargreaves-Samani
   */
  static calcularRadiacaoExtraterrestreEquivalente(latitude, data) {
    const Ra_MJ = this.calcularRadiacaoExtraterrestre(latitude, data);
    // Converter MJ/m²/dia para mm/dia (1 MJ ≈ 0.408 mm)
    return 0.408 * Ra_MJ;
  }
  
  /**
   * Estimar radiação solar (Rs) com base em amplitude térmica
   * Método de Hargreaves (usado quando Rs não é medida)
   */
  static estimarRadiacaoSolar(tempMax, tempMin, latitude, data) {
    const Ra = this.calcularRadiacaoExtraterrestre(latitude, data);
    const amplitudeTermica = tempMax - tempMin;
    
    // Rs = kRs × √(Tmax - Tmin) × Ra
    // kRs = 0.16 para regiões interiores, 0.19 para regiões costeiras
    const kRs = 0.17; // Valor médio para Brasil
    
    const Rs = kRs * Math.sqrt(amplitudeTermica) * Ra;
    return Math.max(0, Rs);
  }
  
  /**
   * Calcular dia juliano (1-365 ou 366)
   */
  static calcularDiaJuliano(data) {
    const inicioAno = new Date(data.getFullYear(), 0, 0);
    const diff = data - inicioAno;
    const umDia = 1000 * 60 * 60 * 24;
    return Math.floor(diff / umDia);
  }
  
  /**
   * Validar temperatura (valores razoáveis: -10°C a 55°C)
   */
  static validarTemperatura(temp) {
    return temp !== null && 
           temp !== undefined && 
           !isNaN(temp) && 
           temp >= -10 && 
           temp <= 55;
  }
  
  /**
   * Processar dados de sensor para obter ET0 diária
   * Agrega dados horários/sub-diários
   */
  static processarDadosSensor(dadosSensor, latitude, altitude) {
    if (!dadosSensor || dadosSensor.length === 0) {
      return null;
    }
    
    // Extrair temperaturas do dia
    const temperaturas = dadosSensor
      .map(d => d.temp_c)
      .filter(t => t !== null && !isNaN(t));
    
    if (temperaturas.length === 0) {
      return null;
    }
    
    const tempMax = Math.max(...temperaturas);
    const tempMin = Math.min(...temperaturas);
    
    // Umidade (se disponível)
    const umidades = dadosSensor
      .map(d => d.rh_perc)
      .filter(u => u !== null && !isNaN(u));
    const umidadeMedia = umidades.length > 0 
      ? umidades.reduce((a, b) => a + b, 0) / umidades.length 
      : null;
    
    // Calcular ET0
    const resultado = this.calcularET0Automatico({
      tempMax,
      tempMin,
      umidade: umidadeMedia,
      vento: null,
      radiacao: null,
      latitude,
      altitude,
      data: new Date(dadosSensor[0].timestamp || new Date())
    });
    
    return resultado;
  }
}

// Valores de referência Kc por cultura e fase (FAO-56)
export const KC_CULTURAS = {
  milho: {
    inicial: 0.30,
    desenvolvimento: 0.70,
    meio: 1.20,
    final: 0.60,
    // Duração das fases (dias após plantio)
    fases: {
      inicial: 25,
      desenvolvimento: 40,
      meio: 45,
      final: 30
    }
  },
  soja: {
    inicial: 0.40,
    desenvolvimento: 0.70,
    meio: 1.15,
    final: 0.50,
    fases: {
      inicial: 20,
      desenvolvimento: 30,
      meio: 45,
      final: 25
    }
  },
  feijao: {
    inicial: 0.40,
    desenvolvimento: 0.70,
    meio: 1.15,
    final: 0.35,
    fases: {
      inicial: 20,
      desenvolvimento: 30,
      meio: 30,
      final: 15
    }
  },
  cafe: {
    inicial: 0.90,
    desenvolvimento: 0.90,
    meio: 1.05,
    final: 1.05,
    fases: {
      inicial: 60,
      desenvolvimento: 90,
      meio: 120,
      final: 95
    }
  },
  trigo: {
    inicial: 0.30,
    desenvolvimento: 0.70,
    meio: 1.15,
    final: 0.40,
    fases: {
      inicial: 20,
      desenvolvimento: 30,
      meio: 50,
      final: 30
    }
  },
  algodao: {
    inicial: 0.35,
    desenvolvimento: 0.70,
    meio: 1.15,
    final: 0.70,
    fases: {
      inicial: 30,
      desenvolvimento: 50,
      meio: 55,
      final: 45
    }
  },
  cana: {
    inicial: 0.40,
    desenvolvimento: 0.75,
    meio: 1.25,
    final: 0.75,
    fases: {
      inicial: 35,
      desenvolvimento: 70,
      meio: 180,
      final: 80
    }
  },
  tomate: {
    inicial: 0.60,
    desenvolvimento: 0.90,
    meio: 1.15,
    final: 0.80,
    fases: {
      inicial: 30,
      desenvolvimento: 40,
      meio: 45,
      final: 25
    }
  },
  batata: {
    inicial: 0.50,
    desenvolvimento: 0.75,
    meio: 1.15,
    final: 0.85,
    fases: {
      inicial: 25,
      desenvolvimento: 30,
      meio: 45,
      final: 25
    }
  },
  hortaliças: {
    inicial: 0.70,
    desenvolvimento: 0.90,
    meio: 1.05,
    final: 0.95,
    fases: {
      inicial: 20,
      desenvolvimento: 30,
      meio: 30,
      final: 15
    }
  }
};

/**
 * Determinar Kc baseado em dias após plantio e cultura
 */
export function obterKcPorFase(cultura, diasAposPlantio) {
  const kcCultura = KC_CULTURAS[cultura?.toLowerCase()] || KC_CULTURAS.milho;
  
  const { fases, inicial, desenvolvimento, meio, final } = kcCultura;
  
  const fimInicial = fases.inicial;
  const fimDesenvolvimento = fimInicial + fases.desenvolvimento;
  const fimMeio = fimDesenvolvimento + fases.meio;
  const fimFinal = fimMeio + fases.final;
  
  if (diasAposPlantio <= fimInicial) {
    return { kc: inicial, fase: 'Inicial' };
  } else if (diasAposPlantio <= fimDesenvolvimento) {
    // Interpolação linear
    const progresso = (diasAposPlantio - fimInicial) / fases.desenvolvimento;
    const kc = inicial + (desenvolvimento - inicial) * progresso;
    return { kc: parseFloat(kc.toFixed(2)), fase: 'Desenvolvimento' };
  } else if (diasAposPlantio <= fimMeio) {
    const progresso = (diasAposPlantio - fimDesenvolvimento) / fases.meio;
    const kc = desenvolvimento + (meio - desenvolvimento) * progresso;
    return { kc: parseFloat(kc.toFixed(2)), fase: 'Meio de Temporada' };
  } else if (diasAposPlantio <= fimFinal) {
    const progresso = (diasAposPlantio - fimMeio) / fases.final;
    const kc = meio + (final - meio) * progresso;
    return { kc: parseFloat(kc.toFixed(2)), fase: 'Final' };
  } else {
    return { kc: final, fase: 'Maturação' };
  }
}
